import {useEffect, useState} from 'react'
import useEffectEvent from './use-effect-event'

/**
 * Tracks vertical scroll to drive an auto-hide header.
 *
 * Returns `true` when the header should be hidden — the user has scrolled down
 * past `threshold` — and `false` when it should be shown (scrolling up, or while
 * still within `threshold` of the top). Scroll reads are throttled with
 * `requestAnimationFrame`, the listener is `passive`, and the hook is SSR-safe
 * (no work runs without a `window`).
 *
 * @param {Object} [options] - Optional configuration.
 * @param {number} [options.threshold=0] - Distance from the top (px) the user
 *        must scroll past before the header is allowed to hide. At or above the
 *        top within this distance the header always stays shown.
 * @param {boolean} [options.disabled=false] - When `true` the header is forced
 *        shown and no scroll listener is attached (e.g. while navigation is open).
 * @param {function(): boolean} [options.isRevealLocked] - Consulted on every
 *        scroll-up frame. While it returns `true` a scroll up does not reveal
 *        the header. It never *forces* the header hidden, so one already shown
 *        stays shown — that is what lets a page hold the header back only until
 *        it has come out on its own. Called inside the scroll frame, so keep it
 *        cheap; it is not called while scrolling down.
 * @returns {boolean} Whether the header should be hidden.
 *
 * @example
 * const hidden = useHideOnScroll({threshold: headerHeight, disabled: isMenuOpen})
 * return <header data-hidden={hidden}>...</header>
 */
const useHideOnScroll = ({threshold = 0, disabled = false, isRevealLocked} = {}) => {
    const [hidden, setHidden] = useState(false)

    // Polled in the scroll frame so the newest answer decides, and wrapped so
    // an inline callback never restarts the listener.
    const revealLocked = useEffectEvent(() => isRevealLocked?.() === true)

    useEffect(() => {
        if (disabled || typeof window === 'undefined') {
            setHidden(false)
            return undefined
        }

        let lastY = window.scrollY
        let frame = null

        // A threshold that arrives after the first scroll — measured, or read
        // from a stylesheet — can land above the current offset, meaning the
        // scroll that hid the header was inside the zone where it should have
        // stayed. Nothing else would clear that until the next scroll up.
        if (lastY <= threshold) {
            setHidden(false)
        }

        const update = () => {
            frame = null
            const y = window.scrollY
            if (y <= threshold) {
                setHidden(false)
            } else if (y > lastY) {
                setHidden(true)
            } else if (y < lastY && !revealLocked()) {
                setHidden(false)
            }
            lastY = y
        }

        const onScroll = () => {
            if (frame === null) {
                frame = window.requestAnimationFrame(update)
            }
        }

        window.addEventListener('scroll', onScroll, {passive: true})
        return () => {
            window.removeEventListener('scroll', onScroll)
            if (frame !== null) {
                window.cancelAnimationFrame(frame)
            }
        }
    }, [threshold, disabled, revealLocked])

    return hidden
}

export default useHideOnScroll
